import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/navigation/Sidebar';
import PatientContextBar from '../../components/navigation/PatientContextBar';
import QuickActions from '../../components/navigation/QuickActions';
import SessionManager from '../../components/navigation/SessionManager';
import Icon from '../../components/AppIcon';

import DentalChart from './components/DentalChart';
import TreatmentTracking from './components/TreatmentTracking';
import ClinicalNotes from './components/ClinicalNotes';
import EPrescribing from './components/EPrescribing';
import ImagingIntegration from './components/ImagingIntegration';

const DentalChartingClinicalWorkflow = () => {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('charting');
  const [selectedTooth, setSelectedTooth] = useState(null);
  const [chartingMode, setChartingMode] = useState('existing'); // existing, planned

  const currentPatient = {
    name: "Sarah Johnson",
    mrn: "MRN-2024-789456",
    dob: "03/15/1978",
    age: "48 years"
  };

  const patientAlerts = [
    {
      id: 1,
      severity: "warning",
      title: "Medication Interaction Alert",
      message: "Patient is taking anticoagulants - consult before invasive procedures",
      dismissible: false
    },
    {
      id: 2,
      severity: "info",
      title: "Periodontal Maintenance Due",
      message: "Last periodontal maintenance was 3 months ago",
      dismissible: true
    }
  ];

  const tabs = [
    { id: 'charting', label: 'Dental Chart', icon: 'Grid3x3' },
    { id: 'treatment', label: 'Treatment Tracking', icon: 'ClipboardList' },
    { id: 'notes', label: 'Clinical Notes', icon: 'FileText' },
    { id: 'prescribe', label: 'E-Prescribing', icon: 'Pill' },
    { id: 'imaging', label: 'Imaging', icon: 'Image' }
  ];

  const handleAlertClick = (alertId) => {
    console.log('Alert clicked:', alertId);
  };

  const handleSessionExpired = () => {
    console.log('Session expired');
    navigate('/login');
  };

  const handleSessionExtended = () => {
    console.log('Session extended');
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden pt-16">
      <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="sticky top-0 z-30 bg-card border-b border-border">
          <PatientContextBar patient={currentPatient} alerts={patientAlerts} onAlertClick={handleAlertClick} />
          <div className="px-4 lg:px-6 py-3 flex items-center justify-between">
            <div>
              <h1 className="text-xl lg:text-2xl font-bold text-foreground">Clinical Workflow</h1>
              <p className="text-sm text-muted-foreground mt-1">Comprehensive charting and treatment documentation</p>
            </div>
            <div className="flex items-center gap-3">
              <QuickActions />
              <SessionManager onSessionExpired={handleSessionExpired} onSessionExtended={handleSessionExtended} />
            </div>
          </div>
        </header>

        {/* Tab Navigation */}
        <div className="bg-card border-b border-border px-4 lg:px-6">
          <div className="flex items-center gap-1 overflow-x-auto">
            {tabs?.map((tab) => (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon name={tab?.icon} size={16} />
                <span className="hidden md:inline">{tab?.label}</span>
              </button>
            ))}
          </div>
        </div>

        <main className="flex-1 overflow-y-auto p-4 lg:p-6">
          {activeTab === 'charting' && (
            <DentalChart 
              selectedTooth={selectedTooth}
              onToothSelect={setSelectedTooth}
              chartingMode={chartingMode}
              onModeChange={setChartingMode}
            />
          )}
          
          {activeTab === 'treatment' && (
            <TreatmentTracking patientId={currentPatient?.mrn} />
          )}
          
          {activeTab === 'notes' && (
            <ClinicalNotes patientId={currentPatient?.mrn} />
          )}
          
          {activeTab === 'prescribe' && (
            <EPrescribing patient={currentPatient} />
          )}
          
          {activeTab === 'imaging' && (
            <ImagingIntegration patientId={currentPatient?.mrn} />
          )}
        </main>
      </div>
    </div>
  );
};

export default DentalChartingClinicalWorkflow;